<script lang="ts" setup>
import {
  IonContent,
  IonHeader,
  IonToolbar,
  IonPage,
  IonButton,
  IonFooter,
  toastController,
} from "@ionic/vue";
import { useI18n } from "@/composables/useI18n";
import { ref } from "vue";
import BackButton from "@/components/ui/BackButton.vue";
import { useRouter } from "vue-router";
import { useBarcodeScanner } from "@/composables/useBarcodeScanner";
import { nextTick } from "vue";
import { useFabriqStore } from "@/store/fabriq";
import { configureApplicationFromQRCode } from "@/router";
import { localLogger } from "@/utils/localLogger";
import { Barcode } from "@capacitor-mlkit/barcode-scanning";

const router = useRouter();
const fabriqStore = useFabriqStore();
const { t } = useI18n();

const image1 = ref();
const image2 = ref();
const loading = ref(false);

function handleImageClick(image: any) {
  image.$viewer.show();
}

async function handleBarcodeScanned(code: Barcode) {
  if (!code) {
    return;
  }
  await localLogger.log(`QR code scanned: ${code}`);
  loading.value = true;
  try {
    fabriqStore.setInitializationState({ type: "pending" });
    await nextTick();
    const url = code?.displayValue;
    const qrCodeUrl = new URL(url);
    const subdomain = qrCodeUrl.searchParams.get("subdomain");
    if (!subdomain) throw new Error("Subdomain not found in QR code");
    await configureApplicationFromQRCode(url);
    router.replace({ name: "Login" });
  } catch (e) {
    const toast = await toastController.create({
      message: t("common.errorWithConfigurationQRCode"),
      duration: 2000,
      color: "danger",
    });
    toast.present();
    loading.value = false;
    await localLogger.log(
      `Error while configuring application from QR code: ${JSON.stringify(e)}`
    );
  }
}

const { openBarcodeScanner } = useBarcodeScanner(handleBarcodeScanned);
</script>
<template>
  <ion-page>
    <ion-header mode="ios">
      <ion-toolbar class="transparent-toolbar ion-padding-start" mode="ios">
        <div class="page-toolbar">
          <back-button
            @back="router.push('ApplicationConfigurationIndex')"
            :disabled="loading"
          />
        </div>
      </ion-toolbar>
    </ion-header>
    <ion-content :fullscreen="true" :scroll-y="false" class="hide-scrollbar">
      <div class="app-configuration-help-content">
        <div class="app-configuration-help-hint">
          <div>{{ t("common.findApplicationConfiguration") }}</div>
          <pre class="ion-padding app-configuration-text-container">{{
            t("common.helpApplicationQRCode")
          }}</pre>
        </div>
        <div class="app-configuration-images">
          <img
            ref="image1"
            v-viewer.static="{
              navbar: 0,
              toolbar: 0,
              transition: false,
              movable: false,
            }"
            load="lazy"
            src="/img/display-QRCode-help-step-1.png"
            @click.stop.prevent="handleImageClick(image1)"
          />
          <img
            ref="image2"
            v-viewer.static="{
              navbar: 0,
              toolbar: 0,
              transition: false,
              movable: false,
            }"
            load="lazy"
            src="/img/display-QRCode-help-step-2.png"
            @click.stop.prevent="handleImageClick(image2)"
          />
        </div>
      </div>
    </ion-content>
    <ion-footer class="ion-no-border">
      <div class="app-configuration-action-button-container">
        <ion-button
          class="ion-margin-top"
          color="secondary"
          mode="ios"
          :disabled="loading"
          @click="openBarcodeScanner"
        >
          <div class="app-configuration-button-content" v-if="loading">
            <font-icon color="#ffffff" name="spinner" spin size="0.3" />
            <div>
              {{ t("common.loading") }}
            </div>
          </div>
          <div class="app-configuration-button-content" v-else>
            <font-icon color="#ffffff" name="qrcode" size="1.375" />
            <div>
              {{ t("common.qrcode") }}
            </div>
          </div>
        </ion-button>
      </div>
    </ion-footer>
  </ion-page>
</template>

<style scoped>
.app-configuration-images {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--ion-padding);
  margin: calc(var(--ion-padding) * 2);
}
.app-configuration-images img {
  max-height: 20vh;
  border-radius: var(--f-border-radius);
}
</style>
